// src/features/quality/components/RevisionStatusBadge.tsx
// ADR-091 Rule QC-FE-03: (status, approval_state) map to ONE combined
// label/color, never two separate badges the user has to mentally combine.

import type {
  ApprovalState,
  RevisionLifecycleStatus,
} from "../types";

export interface RevisionLifecycleLabel {
  label: string;
  colorToken: "gray" | "yellow" | "red" | "green" | "blue" | "darkGray" | "warning";
}

/**
 * Pure function — deliberately exported and unit-tested independently of
 * the React component. See ADR-091 §4 Rule QC-FE-03 table for the source
 * of truth this implements.
 */
export function resolveRevisionLifecycleLabel(
  status: RevisionLifecycleStatus,
  approvalState: ApprovalState
): RevisionLifecycleLabel {
  if (status === "draft" && approvalState === "not_required") {
    return { label: "پیش‌نویس", colorToken: "gray" };
  }
  if (status === "draft" && approvalState === "pending") {
    return { label: "در انتظار تأیید", colorToken: "yellow" };
  }
  if (status === "draft" && approvalState === "rejected") {
    return {
      label: "رد شده — برای اصلاح، نسخهٔ جدید بسازید",
      colorToken: "red",
    };
  }
  if (status === "published" && approvalState === "approved") {
    return { label: "منتشرشده", colorToken: "green" };
  }
  if (status === "superseded") {
    return { label: "جایگزین‌شده", colorToken: "blue" };
  }
  if (status === "archived") {
    return { label: "آرشیوشده", colorToken: "darkGray" };
  }

  // Combination that should never occur per backend logic
  // (apps/quality/inspection/consumers.py::_on_quality_gate_outcome is the
  // only writer of `status`, and it always pairs status=PUBLISHED with
  // approval_state="approved"). If this fires, it's a backend bug — don't
  // swallow it silently.
  // eslint-disable-next-line no-console
  console.warn(
    `[quality] unexpected status/approval_state combination: ` +
      `status="${status}", approval_state="${approvalState}". ` +
      `This should not happen — check the workflow gate consumer.`
  );
  return { label: "وضعیت نامشخص", colorToken: "warning" };
}

const COLOR_STYLES: Record<
  RevisionLifecycleLabel["colorToken"],
  string
> = {
  gray: "bg-slate-400/15 text-slate-400",
  yellow: "bg-yellow-500/15 text-yellow-500",
  red: "bg-red-500/15 text-red-500",
  green: "bg-green-500/15 text-green-500",
  blue: "bg-blue-400/15 text-blue-400",
  darkGray: "bg-slate-500/25 text-slate-500",
  warning: "bg-orange-500/20 text-orange-500",
};

export interface RevisionStatusBadgeProps {
  status: RevisionLifecycleStatus;
  approvalState: ApprovalState;
}

export function RevisionStatusBadge({
  status,
  approvalState,
}: RevisionStatusBadgeProps) {
  const { label, colorToken } = resolveRevisionLifecycleLabel(
    status,
    approvalState
  );
  const colorClass = COLOR_STYLES[colorToken];

  return (
    <span
      data-testid="revision-status-badge"
      className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}
    >
      {label}
    </span>
  );
}
